import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, X, Calendar, Users, Clock, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";

/**
 * AI Leave Coach modal — asks Claude Sonnet 4.5 to suggest the top 3 best
 * windows to take leave given team coverage, holidays, manager load and
 * personal balance. `onApply({start_date, end_date})` fires when the user
 * picks a suggestion so the parent form can pre-fill.
 */
export default function LeaveCoachModal({ open, onClose, onApply }) {
  const [daysNeeded, setDaysNeeded] = useState(5);
  const [windowDays, setWindowDays] = useState(90);
  const [leaveType, setLeaveType] = useState("annual");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/hr/leave-coach/suggest", {
        days_needed: Number(daysNeeded),
        window_days: Number(windowDays),
        leave_type: leaveType,
      });
      setResult(data);
      if (!data.suggestions?.length) {
        toast.info("No windows fit — try a wider search or fewer days.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Coach unavailable");
    } finally {
      setBusy(false);
    }
  };

  const apply = (s) => {
    onApply?.({ start_date: s.start_date, end_date: s.end_date });
    toast.success(`Dates filled: ${s.start_date} → ${s.end_date}`);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-8 sm:pt-16 px-4"
      data-testid="leave-coach-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-zinc-950 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-zinc-950 text-white px-6 py-4 flex items-center justify-between sticky top-0">
          <div className="flex items-center gap-3">
            <Sparkle size={20} weight="fill" className="text-[#FF3E00]" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-[#FF3E00]">SMARTERP / AI COACH</div>
              <div className="font-display text-lg font-black tracking-tight">Find your best leave window</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="leave-coach-close-btn" className="text-white/70 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Days needed</span>
              <input
                type="number"
                min="0.5"
                max="30"
                step="0.5"
                value={daysNeeded}
                onChange={(e) => setDaysNeeded(e.target.value)}
                data-testid="coach-days-input"
                className="mt-1 w-full border border-zinc-950 px-3 py-2 text-sm tnum"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Search window (days)</span>
              <input
                type="number"
                min="7"
                max="365"
                step="7"
                value={windowDays}
                onChange={(e) => setWindowDays(e.target.value)}
                data-testid="coach-window-input"
                className="mt-1 w-full border border-zinc-950 px-3 py-2 text-sm tnum"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Leave type</span>
              <select
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                data-testid="coach-type-select"
                className="mt-1 w-full border border-zinc-950 px-3 py-2 text-sm bg-white"
              >
                <option value="annual">Annual</option>
                <option value="sick">Sick</option>
                <option value="unpaid">Unpaid</option>
                <option value="maternity">Maternity</option>
              </select>
            </label>
          </div>

          <button
            onClick={run}
            disabled={busy}
            data-testid="coach-suggest-btn"
            className="w-full bg-[#FF3E00] text-white font-mono text-xs tracking-[0.15em] uppercase font-bold py-3 hover:bg-zinc-950 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            <Sparkle size={14} weight="fill" />
            {busy ? "COACH THINKING…" : "SUGGEST BEST WINDOWS"}
          </button>

          {result && (
            <div className="mt-6" data-testid="coach-results">
              <div className="mb-4 text-xs text-zinc-500 font-mono tracking-wider">
                {result.department ? `DEPT: ${result.department.toUpperCase()} · ` : ""}
                BALANCE LEFT: <span className="text-zinc-950 font-bold">{result.balance_left ?? "—"}d</span>
              </div>
              {result.suggestions.length === 0 && (
                <div className="border border-dashed border-zinc-300 p-6 text-center text-xs font-mono text-zinc-500">
                  NO SUITABLE WINDOW FOUND · WIDEN THE SEARCH OR REDUCE DAYS
                </div>
              )}
              <div className="space-y-4">
                {result.suggestions.map((s, i) => (
                  <div
                    key={s.start_date}
                    data-testid={`coach-suggestion-${i}`}
                    className="border border-zinc-950 hover:border-[#FF3E00] transition-colors"
                  >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#FF3E00] text-white font-mono text-xs font-black">#{i + 1}</div>
                        <div>
                          <div className="font-display text-lg font-black tracking-tight tnum">
                            {s.start_date} → {s.end_date}
                          </div>
                          <div className="text-[10px] font-mono tracking-wider text-zinc-500">
                            SCORE {s.score}/100 · {s.calendar_days}d OFF FOR {s.leave_days_used}d LEAVE
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => apply(s)}
                        data-testid={`coach-apply-btn-${i}`}
                        className="bg-zinc-950 text-white font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2 hover:bg-[#FF3E00] flex items-center gap-1"
                      >
                        <CheckCircle size={12} weight="fill" /> USE THESE DATES
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 py-3 text-[11px] font-mono tracking-wider">
                      <div className="flex items-center gap-1.5 text-zinc-600">
                        <Calendar size={12} />
                        {s.holidays_inside.length} HOLIDAY{s.holidays_inside.length !== 1 ? "S" : ""}
                      </div>
                      <div className="flex items-center gap-1.5 text-zinc-600">
                        <Users size={12} />
                        {s.team_conflicts} CONFLICT{s.team_conflicts !== 1 ? "S" : ""}
                      </div>
                      <div className="flex items-center gap-1.5 text-zinc-600">
                        <Clock size={12} />
                        MGR: {s.manager_pending_load}
                      </div>
                      <div className="flex items-center gap-1.5 text-zinc-600">
                        <Sparkle size={12} />
                        +{s.stretch_days}d FREE
                      </div>
                    </div>
                    {s.narrative && (
                      <div className="px-4 py-3 text-sm text-zinc-700 leading-relaxed border-t border-zinc-100 bg-white">
                        {s.narrative}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
